import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useApplyPayment } from '@/services/paymentService';
import { ApplyPaymentResponse, PAYMENT_MODE_OPTIONS, PaymentMode } from '@/types/payment.types';
import { useEffect, useMemo } from 'react';
import { displayError } from '@/lib/error';
import { toast } from './ui/use-toast';
import { ApplyPaymentFormData, ApplyPaymentSchema } from '@pages/accounts/accounts.schema';

interface InvoiceForPayment {
  invoiceNumber: string;
  invoiceAmount: number;
  balance?: number | null;
}

interface ApplyPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: InvoiceForPayment[];
  onSuccess?: (data: ApplyPaymentResponse) => void;
}

const ApplyPaymentModal = ({ open, onOpenChange, invoices, onSuccess }: ApplyPaymentModalProps) => {
  const applyPaymentMutation = useApplyPayment();

  const totalInvoiceAmount = useMemo(() => {
    return invoices.reduce((sum, inv) => sum + inv.invoiceAmount, 0);
  }, [invoices]);

  const totalBalanceAmount = useMemo(() => {
    return invoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
  }, [invoices]);

  const invoiceNumbers = useMemo(() => {
    return invoices.map((inv) => inv.invoiceNumber);
  }, [invoices]);

  const form = useForm<ApplyPaymentFormData>({
    resolver: zodResolver(ApplyPaymentSchema),
    mode: 'onChange',
    defaultValues: {
      paymentMode: '',
      paymentAmount: '0',
      balance: totalBalanceAmount.toFixed(2),
      memo: '',
      checkNumber: '',
      invoiceNumbers: [],
    },
  });
  const { control, setValue, getValues, trigger } = form;
  const paymentAmount = useWatch({ control, name: 'paymentAmount' });
  const balance = useWatch({ control, name: 'balance' });
  const paymentMode = useWatch({ control, name: 'paymentMode' });

  useEffect(() => {
    if (open) {
      form.reset({
        paymentMode: '',
        paymentAmount: '0',
        balance: totalBalanceAmount.toFixed(2),
        memo: '',
        checkNumber: '',
        invoiceNumbers: invoiceNumbers,
        lastInvoiceNumber: '',
        paymentDate: undefined,
      });
    }
  }, [open, invoiceNumbers, totalBalanceAmount, form]);

  useEffect(() => {
    const amount = parseFloat(paymentAmount ?? '0');
    if (!isNaN(amount)) {
      const calculatedBalance = (totalBalanceAmount - amount).toFixed(2);
      setValue('balance', calculatedBalance, { shouldValidate: true });
    }
  }, [paymentAmount, totalBalanceAmount, setValue]);

  const hasBalance = useMemo(() => {
    const bal = parseFloat(balance ?? '0');
    return bal !== 0;
  }, [balance]);

  const isMultipleInvoices = invoices.length > 1;
  const isMemoRequired = hasBalance;
  const isLastInvoiceRequired = isMultipleInvoices && hasBalance;

  const handleSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    const data = getValues();
    const trimmedLastInvoiceNumber = data.lastInvoiceNumber?.trim();
    const lastInvoiceNumber = trimmedLastInvoiceNumber === '' ? undefined : trimmedLastInvoiceNumber;

    applyPaymentMutation.mutate(
      {
        invoiceNumbers,
        paymentMode: data.paymentMode as PaymentMode,
        paymentDate: data?.paymentDate,
        paymentAmount: parseFloat(data.paymentAmount ?? '0'),
        totalInvoiceAmount,
        balance: parseFloat(data.balance ?? '0'),
        memo: data.memo ?? undefined,
        checkNumber: data.checkNumber ?? undefined,
        lastInvoiceNumber,
      },
      {
        onSuccess: (data) => {
          toast({
            description: 'Payment applied successfully',
          });
          onOpenChange(false);
          onSuccess?.(data);
        },
        onError: (error) => {
          displayError(error);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[60%] max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex-shrink-0">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Apply Payment</DialogTitle>
            <DialogDescription className="sr-only">Apply payment to selected invoices</DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 flex-col bg-gray-50 p-4 rounded-lg mt-4">
            <div className="text-sm font-medium">
              <span className="text-gray-600">Invoice(s) #: </span>
              <span className="text-gray-900">{invoiceNumbers.join(', ')}</span>
            </div>
            <div className="text-sm font-medium">
              <span className="text-gray-600">Total Amount: </span>
              <span className="text-[#551175] font-semibold">{formatCurrency(totalInvoiceAmount)}</span>
            </div>
            <div className="text-sm font-medium">
              <span className="text-gray-600">Balance: </span>
              <span className="text-[#551175] font-semibold">{formatCurrency(totalBalanceAmount)}</span>
            </div>
          </div>
        </div>

        <FormProvider {...form}>
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto py-4 custom-scrollbar">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <FormFieldSelect
                    name="paymentMode"
                    label="Mode of Payment"
                    placeholder="Select Mode of Payment"
                    options={PAYMENT_MODE_OPTIONS.map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                    }))}
                    required
                    onChange={() => {
                      setValue('paymentDate', null);
                      setValue('checkNumber', null);
                    }}
                    className="focus:border-[#1551B2]"
                  />
                </div>

                {(paymentMode === 'CHECK' || paymentMode === 'E_CHECK') && (
                  <div className="col-span-6">
                    <FormFieldComponent
                      control={control}
                      name="checkNumber"
                      label="Check #"
                      placeholder="Enter Check Number"
                      required
                      maxLength={20}
                      allowSpecialCharacters={false}
                      replaceRegex={/[^0-9]/g}
                    />
                  </div>
                )}

                {paymentMode && paymentMode !== 'CHECK' && paymentMode !== 'E_CHECK' && (
                  <div className="col-span-6">
                    <FormFieldCalendar
                      name="paymentDate"
                      label="Payment Date"
                      placeholder="Select Date"
                      required
                      control={control}
                      disabledAt={(date: Date) => {
                        const today = new Date();
                        return date > today;
                      }}
                    />
                  </div>
                )}

                {paymentMode && (
                  <>
                    <div className="col-span-6">
                      <FormFieldComponent
                        control={control}
                        name="paymentAmount"
                        label="Amount"
                        placeholder="Enter Amount"
                        required
                        type="number"
                        allowSpecialCharacters={false}
                      />
                    </div>

                    <div className="col-span-6">
                      <FormFieldComponent
                        control={control}
                        name="balance"
                        label="Balance"
                        placeholder="Enter Balance"
                        disabled
                      />
                    </div>

                    {hasBalance && (
                      <div className="col-span-12">
                        <FormFieldComponent
                          control={control}
                          name="memo"
                          label="Memo"
                          placeholder="Enter Memo Notes"
                          required={isMemoRequired}
                        />
                      </div>
                    )}

                    {isMultipleInvoices && hasBalance && (
                      <div className="col-span-12">
                        <FormFieldComponent
                          name="lastInvoiceNumber"
                          control={control}
                          label="Last Invoice (receives the balance)"
                          placeholder="Select Invoice"
                          required={isLastInvoiceRequired}
                          defaultValue={invoices.length ? invoices[invoices.length - 1].invoiceNumber : ''}
                          className="focus:border-[#1551B2]"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 w-full flex justify-end gap-4 pt-4 border-t">
              <Button type="button" className="w-[75px] h-[36px]" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  void handleSubmit();
                }}
                variant="default"
                className="w-[134px] h-[36px]"
                disabled={applyPaymentMutation.isPending}
              >
                {'Review and Pay'}
              </Button>
            </div>
          </div>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyPaymentModal;
